import axios from 'axios'
import { extractApiError } from '@/utils/extractApiError'

const FRIENDLY_BY_STATUS: Record<number, string> = {
  401: 'Sessão expirada. Faça login novamente.',
  403: 'Você não tem permissão para esta ação.',
  404: 'Recurso não encontrado.',
  409: 'Conflito: o recurso já existe ou foi alterado.',
  429: 'Muitas tentativas. Aguarde um momento e tente de novo.',
  503: 'Serviço temporariamente indisponível. Atualize a página ou avise o administrador.',
}

/** Códigos da API (`error.code`) — BDR / Closer / leads. */
const FRIENDLY_BY_CODE: Record<string, string> = {
  EMAIL_REQUIRED: 'Informe o e-mail do contato para agendar a reunião.',
  DORES_REQUIRED: 'Descreva as dores/problema do lead (mínimo 10 caracteres).',
  DATA_REUNIAO_REQUIRED: 'Informe a data e hora da reunião.',
  DATA_REUNIAO_INVALID: 'Data ou hora da reunião inválida. Verifique e tente novamente.',
  CLOSER_REQUIRED: 'Selecione o Closer para agendar a reunião.',
  NOT_FOUND: 'Registro não encontrado. Atualize a página e tente novamente.',
  CONFLICT: 'Já existe um cadastro com este telefone ou e-mail.',
  SCHEMA_OUTDATED: 'Banco de dados desatualizado. Avise o administrador para rodar as migrations.',
  REUNIAO_REQUIRED: 'Informe a data da reunião para concluir o handoff.',
  REUNIAO_NOT_CONFIRMED: 'Confirme a reunião como realizada antes de avançar.',
  INVALID_TRANSITION: 'Transição de etapa não permitida para este deal.',
}

/** Códigos em que a mensagem da API costuma ser mais específica que o texto fixo. */
const PREFER_API_MESSAGE_CODES = new Set(['VALIDATION_ERROR', 'CONFLICT', 'RESSALVAS_REQUIRED'])

const DEFAULT_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente.'

const VALIDATION_FIELD_LABELS: Record<string, string> = {
  nomeCompleto: 'Nome completo',
  email: 'E-mail',
  telefone: 'Telefone',
  cpf: 'CPF',
  cnpj: 'CNPJ',
  razaoSocial: 'Razão social',
  cep: 'CEP',
  logradouro: 'Logradouro',
  numero: 'Número',
  complemento: 'Complemento',
  bairro: 'Bairro',
  cidade: 'Cidade',
  uf: 'UF',
  bancoNome: 'Banco',
  bancoAgencia: 'Agência',
  bancoConta: 'Conta',
  bancoTipoConta: 'Tipo de conta',
  portalUserId: 'Usuário do portal',
}

function humanizeValidationMessage(message: string): string {
  return message
    .split('; ')
    .map((part) => {
      const separator = part.indexOf(': ')
      if (separator === -1) return part
      const field = part.slice(0, separator)
      const detail = part.slice(separator + 2)
      const label = VALIDATION_FIELD_LABELS[field] ?? field
      return `${label}: ${detail}`
    })
    .join(' · ')
}

/** Mensagem segura para toast/UI — prioriza `error.message` da API em 4xx. */
export function getFriendlyError(error: unknown, fallback = DEFAULT_MESSAGE): string {
  if (!axios.isAxiosError(error)) {
    if (error instanceof Error) {
      if (error.message === 'reuniao_create_failed') {
        return 'Não foi possível registrar a reunião. Tente novamente.'
      }
      if (error.message.trim() && error.message !== fallback) {
        return error.message.trim()
      }
    }
    return fallback
  }

  const extracted = extractApiError(error, fallback)
  const status = error.response?.status

  if (extracted.code && PREFER_API_MESSAGE_CODES.has(extracted.code) && extracted.message !== fallback) {
    if (extracted.code === 'VALIDATION_ERROR') {
      return humanizeValidationMessage(extracted.message)
    }
    return extracted.message
  }

  if (extracted.code && FRIENDLY_BY_CODE[extracted.code]) {
    if (extracted.code === 'CONFLICT' && extracted.message !== fallback) {
      return extracted.message
    }
    return FRIENDLY_BY_CODE[extracted.code]
  }

  if (status === 409) {
    return extracted.message
  }
  if (status && FRIENDLY_BY_STATUS[status]) {
    return FRIENDLY_BY_STATUS[status]
  }

  if (!error.response) {
    return 'Erro de conexão. Verifique sua internet e tente novamente.'
  }

  if (status && status >= 500) {
    const message = extractApiError(error, fallback).message
    if (message && message !== fallback && message !== 'Internal server error') {
      return message
    }
  }

  if (status && status >= 400 && status < 500) {
    const message = extractApiError(error, fallback).message
    if (extracted.code === 'VALIDATION_ERROR') {
      return humanizeValidationMessage(message)
    }
    return message
  }

  return fallback
}
